import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import usePageMeta from "@/hooks/usePageMeta";
import { formatHeat, getRelatedWorks, getWorkById, TYPE_LABELS } from "@/lib/works";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const work = getWorkById(id);
  const [shareNote, setShareNote] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  usePageMeta({
    title: work ? `${work.title} - 痴汉电车剧情与介绍` : "作品未找到 - 痴汉电车",
    description: work
      ? `${TYPE_LABELS[work.type]}《${work.title}》（${work.year}，${work.region}）：${work.synopsis}`
      : "没有找到对应的作品资料，请返回分类页继续浏览。",
    keywords: work
      ? `${work.title},痴汉电车,${work.genres.join(",")},${TYPE_LABELS[work.type]}剧情`
      : "痴汉电车,影视作品",
    suffix: false,
  });

  useEffect(() => {
    if (!shareNote) return undefined;
    const timer = window.setTimeout(() => setShareNote(false), 2600);
    return () => window.clearTimeout(timer);
  }, [shareNote]);

  if (!work) {
    return (
      <SiteLayout>
        <section className="w-full px-4 md:px-8 pt-32 md:pt-40 pb-24">
          <div className="max-w-xl mx-auto text-center">
            <span className="inline-flex w-16 h-16 rounded-full border border-background-300/70 items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-primary-400" />
            </span>
            <h1 className="mt-6 font-heading text-3xl font-semibold text-foreground-950">作品未找到</h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              链接中的作品编号不存在或资料已调整。你可以返回分类页重新浏览，或使用站内搜索查找相关剧情。
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
              <Link
                to="/category"
                className="px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm transition-colors whitespace-nowrap"
              >
                浏览全部作品
              </Link>
              <Link
                to="/search"
                className="px-6 py-3 rounded-md border border-background-300/70 text-foreground-800 hover:border-primary-500 text-sm transition-colors whitespace-nowrap"
              >
                前往搜索
              </Link>
            </div>
          </div>
        </section>
      </SiteLayout>
    );
  }

  const related = getRelatedWorks(work, 4);
  const paragraphs = work.plot.split("\n\n");

  return (
    <SiteLayout>
      <section className="relative w-full pt-28 md:pt-32 pb-16 md:pb-24 px-4 md:px-8">
        <div className="absolute inset-x-0 top-0 h-[320px] bg-gradient-to-b from-primary-900/30 to-transparent pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-300 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${work.type}`} className="hover:text-primary-300 transition-colors">
              {TYPE_LABELS[work.type]}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800 line-clamp-1">{work.title}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-10 lg:gap-14">
            {/* 左侧：大图 + 剧情 */}
            <div className="min-w-0">
              <h1 className="font-heading text-3xl md:text-5xl font-semibold leading-tight text-foreground-950">
                {work.title}
              </h1>
              <p className="mt-3 text-base md:text-lg text-accent-300 leading-relaxed">{work.tagline}</p>

              <div className="mt-7 flex flex-col sm:flex-row gap-6 md:gap-8">
                <div className="w-[200px] md:w-[260px] shrink-0 aspect-[2/3] rounded-lg overflow-hidden border border-background-300/60 bg-background-100">
                  <img
                    src={work.poster}
                    alt={`${work.title} 海报`}
                    title={`${work.title} 剧情介绍`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="px-3 py-1 rounded bg-primary-500 text-foreground-950 text-xs font-medium">
                      {TYPE_LABELS[work.type]}
                    </span>
                    {work.genres.map((genre) => (
                      <Link
                        key={genre}
                        to={`/search?q=${encodeURIComponent(genre)}`}
                        className="px-3 py-1 rounded-full bg-secondary-900/60 border border-secondary-800 text-xs text-foreground-700 hover:border-primary-500 transition-colors"
                      >
                        {genre}
                      </Link>
                    ))}
                  </div>

                  <div className="mt-5 flex items-end gap-5">
                    <div>
                      <p className="font-label text-[11px] tracking-[0.24em] text-foreground-500 uppercase">RATING</p>
                      <p className="font-heading text-3xl text-accent-400 leading-none mt-2">
                        {work.rating.toFixed(1)}
                        <span className="text-sm text-foreground-500 ml-1">/ 10</span>
                      </p>
                    </div>
                    <div className="h-10 w-px bg-background-300/60" />
                    <div>
                      <p className="font-label text-[11px] tracking-[0.24em] text-foreground-500 uppercase">HEAT</p>
                      <p className="font-heading text-xl text-foreground-900 leading-none mt-2">
                        {formatHeat(work.heat)}
                      </p>
                    </div>
                  </div>

                  <dl className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <div>
                      <dt className="text-xs text-foreground-500">上映年份</dt>
                      <dd className="mt-1 text-foreground-900">{work.year} 年</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-foreground-500">地区</dt>
                      <dd className="mt-1 text-foreground-900">{work.region}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-foreground-500">时长 / 集数</dt>
                      <dd className="mt-1 text-foreground-900">{work.duration}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-foreground-500">更新时间</dt>
                      <dd className="mt-1 text-foreground-900">{work.updatedAt}</dd>
                    </div>
                  </dl>

                  <div className="relative mt-7">
                    <button
                      type="button"
                      onClick={() => setShareNote(true)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300/70 text-sm text-foreground-800 hover:border-primary-500 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-share-forward-line" />
                      分享这部作品
                    </button>
                    {shareNote && (
                      <p className="mt-3 text-xs text-foreground-500 animate-fade-in">
                        分享按钮为界面演示，暂未接入外部社交平台。
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-10 md:mt-12">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-[2px] bg-primary-500" />
                  <h2 className="font-heading text-xl md:text-2xl text-foreground-950">剧情简介</h2>
                </div>
                <div className="mt-5 space-y-4">
                  {paragraphs.map((paragraph, index) => (
                    <p key={index} className="text-sm md:text-[15px] leading-[1.9] text-foreground-700">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>

              <div className="mt-10 rounded-lg border border-background-200/70 bg-background-100/60 p-5 md:p-6">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-primary-400 text-lg mt-[2px]" />
                  <div>
                    <h3 className="font-heading text-base text-foreground-900">观看提示</h3>
                    <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                      本页剧情与演职人员信息为站点示范内容，用于展示作品档案结构，不代表任何真实影片。本站不提供片源与播放服务，请通过正规发行渠道观看相关作品。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 右侧：详情 + 相关推荐 */}
            <aside className="min-w-0 flex flex-col gap-6">
              <div className="rounded-lg border border-background-200/70 bg-background-100/70 p-5">
                <h2 className="font-heading text-base tracking-[0.12em] text-foreground-950">作品详情</h2>
                <dl className="mt-4 space-y-4 text-sm">
                  <div>
                    <dt className="text-xs text-foreground-500">导演</dt>
                    <dd className="mt-1 text-foreground-900">{work.director}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-500">主演</dt>
                    <dd className="mt-1 text-foreground-900 leading-relaxed">{work.cast.join(" / ")}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-500">类型</dt>
                    <dd className="mt-1 text-foreground-900">
                      {TYPE_LABELS[work.type]} · {work.genres.join(" / ")}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-500">制片地区</dt>
                    <dd className="mt-1 text-foreground-900">{work.region}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-500">片长</dt>
                    <dd className="mt-1 text-foreground-900">{work.duration}</dd>
                  </div>
                </dl>
                <Link
                  to={`/category/${work.type}`}
                  className="mt-6 flex items-center justify-between gap-2 px-4 py-2.5 rounded-md border border-background-300/60 text-sm text-foreground-800 hover:border-primary-500 transition-colors"
                >
                  浏览全部{TYPE_LABELS[work.type]}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>

              <div className="rounded-lg border border-background-200/70 bg-background-100/70 p-5">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-base tracking-[0.12em] text-foreground-950">相关推荐</h2>
                  <span className="text-[11px] text-foreground-500">同类剧情</span>
                </div>
                <ul className="mt-4 flex flex-col gap-4">
                  {related.map((item) => (
                    <li key={item.id}>
                      <Link to={`/detail/${item.id}`} className="group flex gap-3 cursor-pointer">
                        <div className="w-16 h-[88px] rounded overflow-hidden shrink-0 bg-background-50">
                          <img
                            src={item.poster}
                            alt={`${item.title} 海报`}
                            title={`${item.title} 剧情介绍`}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-heading text-sm text-foreground-900 leading-snug group-hover:text-primary-300 transition-colors line-clamp-2">
                            {item.title}
                          </h3>
                          <p className="mt-1 text-[11px] text-foreground-500">
                            {TYPE_LABELS[item.type]} · {item.year}
                          </p>
                          <p className="mt-1 text-[11px] text-foreground-600 line-clamp-2">{item.genres.join(" · ")}</p>
                        </div>
                        <span className="font-label text-sm text-accent-400 shrink-0">{item.rating.toFixed(1)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}